import React from "react";

import {
  Typography,
  Stack,
  Paper,
  Tabs,
  Tab,
  Divider,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import GroupIcon from "@mui/icons-material/Group";
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import EventIcon from "@mui/icons-material/Event";
import DownloadIcon from "@mui/icons-material/Download";

import AdminLayout from "../../layouts/admin-layout";
import AddClientModal from "./add-client-modal";
import ClientsTable from "./components/admin-clients-table";
import AdminUsersTab from "./components/admin-users-tab";
import AdminMeetingsTab from "./components/admin-meetings-tab";
import AdminSurveyExportTab from "./components/admin-survey-export-tab";
import { useListClients } from "../../store";
import { Client } from "../../types";
import AdminTeamsTab from "./components/admin-teams-tab";

const AdminDashboardPage: React.FC = () => {
  const [selectedClientId, setSelectedClientId] = React.useState<string>("");
  const [selectedTab, setSelectedTab] = React.useState<number>(0);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortBy, setSortBy] = React.useState<string>("clientName");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);
  const [isAddClientOpen, setIsAddClientOpen] = React.useState(false);

  const { data: clients = [], isLoading: clientsLoading } = useListClients();

  React.useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortClients = (clientsToSort: Client[]) => {
    return [...clientsToSort].sort((a, b) => {
      let aValue: any = a[sortBy as keyof Client];
      let bValue: any = b[sortBy as keyof Client];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  const filteredClients = sortClients(
    clients.filter(
      (client) =>
        client.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.clientId?.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  );

  const handleSelectClient = (client: Client) => {
    setSelectedClientId(client.clientId);
    setSelectedTab(0);
  };

  return (
    <AdminLayout>
      <Stack spacing={2}>
        <Paper>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
            <TextField
              placeholder="Search clients by name or ID..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant="contained"
              onClick={() => setIsAddClientOpen(true)}
              startIcon={<AddIcon />}
              sx={{ px: 2 }}
            >
              Add Client
            </Button>
            <AddClientModal
              open={isAddClientOpen}
              onClose={() => setIsAddClientOpen(false)}
            />
          </Stack>

          <ClientsTable
            clients={filteredClients}
            clientsLoading={clientsLoading}
            onSelectClient={handleSelectClient}
            selectedClientId={selectedClientId}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            onPageChange={setCurrentPage}
          />
        </Paper>
        <Divider />
        {!selectedClientId ? (
          <Typography color="text.secondary" textAlign={"center"}>
            Select a client above to view users, teams, and meetings.
          </Typography>
        ) : (
          <Paper>
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  py: 0.25,
                  px: 1.25,
                  fontSize: "0.8rem",
                  textTransform: "none",
                },
                "& .MuiTab-iconWrapper": {
                  marginBottom: 0,
                  marginRight: 0.5,
                },
              }}
            >
              <Tab
                label="Users"
                icon={<GroupIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Teams"
                icon={<WorkspacesIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Meetings"
                icon={<EventIcon fontSize="small" />}
                iconPosition="start"
              />
              <Tab
                label="Survey Export"
                icon={<DownloadIcon fontSize="small" />}
                iconPosition="start"
              />
            </Tabs>

            <Stack>
              {selectedTab === 3 ? (
                <AdminSurveyExportTab selectedClientId={selectedClientId} />
              ) : selectedTab === 0 ? (
                <AdminUsersTab selectedClientId={selectedClientId} />
              ) : selectedTab === 1 ? (
                <AdminTeamsTab selectedClientId={selectedClientId} />
              ) : (
                <AdminMeetingsTab selectedClientId={selectedClientId} />
              )}
            </Stack>
          </Paper>
        )}
      </Stack>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
